import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Cart drawer upsell.
 *
 * Handles the "Ajouter" buttons rendered by snippets/cart-upsell.liquid.
 * It uses the same cart events as Horizon's own add-to-cart (CartLinesUpdateEvent),
 * so the cart drawer, the header cart count and any other listener update on their own.
 */

const ADD_SELECTOR = '[data-cart-upsell-add]';
const ADD_QUANTITY = 1;

/** @returns {string[]} Section ids of every cart-items-component on the page. */
function getCartSectionIds() {
  /** @type {string[]} */
  const ids = [];
  for (const element of document.querySelectorAll('cart-items-component')) {
    if (element instanceof HTMLElement && element.dataset.sectionId) {
      ids.push(element.dataset.sectionId);
    }
  }
  return ids;
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

/**
 * @param {HTMLElement} button
 * @param {string} message
 */
function setError(button, message) {
  const errorElement = button.closest('[data-cart-upsell-item]')?.querySelector('[data-cart-upsell-error]');
  if (!(errorElement instanceof HTMLElement)) return;
  errorElement.textContent = message;
  errorElement.hidden = !message;
}

/** @param {HTMLButtonElement} button */
async function addUpsellItem(button) {
  if (button.dataset.loading === 'true') return;

  const variantId = button.dataset.variantId;
  if (!variantId) return;

  const fallbackError = button.dataset.errorText || 'Impossible d\'ajouter ce produit.';

  button.dataset.loading = 'true';
  button.disabled = true;
  setError(button, '');

  const deferred = CartLinesUpdateEvent.createPromise();
  // Listeners handle rejections themselves; this only avoids an "unhandled rejection" log.
  deferred.promise.catch(() => {});

  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: variantId, quantity: ADD_QUANTITY }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: ADD_QUANTITY }],
        sections: getCartSectionIds().join(','),
      }),
    });
    const data = await response.json();

    if (data.status) {
      // Shopify answers { status, message, description } when the add fails (e.g. not enough stock).
      button.dispatchEvent(
        new CartErrorEvent({
          error: data.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: data.description, errors: data.errors },
        })
      );

      const cart = await fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: true,
          items: cart.items,
          source: 'cart-upsell',
          itemCount: ADD_QUANTITY,
        },
      });

      setError(button, data.description || data.message || fallbackError);
      return;
    }

    const cart = await fetchCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'cart-upsell',
        itemCount: ADD_QUANTITY,
        sections: data.sections,
        didError: false,
      },
    });
  } catch (error) {
    console.error(error);
    deferred.reject(error);
    button.dispatchEvent(
      new CartErrorEvent({
        error: error instanceof Error ? error.message : 'Network error during add to cart',
        code: 'SERVICE_UNAVAILABLE',
      })
    );
    setError(button, fallbackError);
  } finally {
    // On success the drawer is re-rendered and this button is replaced; on failure it must be usable again.
    button.dataset.loading = 'false';
    button.disabled = false;
  }
}

// Delegated listener: the drawer content is re-rendered after every cart change.
document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const button = target.closest(ADD_SELECTOR);
  if (!(button instanceof HTMLButtonElement)) return;

  event.preventDefault();
  addUpsellItem(button);
});